/**
 * Custom hook for fetching company profile statistics
 *
 * Returns aggregated company-level statistics for the Control Hub dashboard.
 * Includes commodity profile, trade routes, partnerships, and container activity.
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// ============================================================================
// TYPES
// ============================================================================

export interface TopHsCode {
    hs_code: string;
    hs_chapter: string | null;
    hs_code_label: string | null;
    shipment_count: number;
    total_value_usd: number;
    role: string;
}

export interface TopTradeRoute {
    origin_country_code: string;
    destination_country_code: string;
    shipment_count: number;
    total_value_usd: number;
}

export interface CompanyProfile {
    company_id: string;
    company_name: string;

    // Commodity profile
    unique_commodities: number;
    commodity_shipments: number;
    commodity_value_usd: number;
    hs_chapters_traded: string[] | null;
    export_commodity_shipments: number;
    import_commodity_shipments: number;

    // Route profile
    unique_routes: number;
    route_shipments: number;
    route_value_usd: number;
    total_weight_kg: number;
    total_volume_cbm: number;
    origin_countries: number;
    destination_countries: number;
    export_route_shipments: number;
    import_route_shipments: number;

    // Partnership profile
    trade_partners: number;
    partnership_shipments: number;
    partnership_value_usd: number;
    strong_partners: number;
    active_partners: number;

    // Container activity
    total_containers: number;
    completed_containers: number;
    containers_value_usd: number;

    // Team
    team_members: number;
    active_team_members: number;

    // Classifications
    profile_completeness_score: number;
    trading_pattern: string;
    volume_tier: string;
    /** True when shipper declared HS + route + carrier in company profile */
    shipper_operational_profile_complete?: boolean;

    // Top items
    top_hs_codes: TopHsCode[];
    top_trade_routes: TopTradeRoute[];

    // Metadata
    refreshed_at: string;
}

// ============================================================================
// QUERY KEY
// ============================================================================

export const COMPANY_PROFILE_QUERY_KEY = ["company-profile"] as const;

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to fetch company profile statistics
 *
 * @param enabled - Whether the query should be enabled (default: true)
 *
 * Returns:
 * - Commodity profile (HS codes, values)
 * - Trade routes (origins, destinations)
 * - Partnership statistics
 * - Container activity summary
 * - Profile classifications (trading pattern, volume tier)
 */
export function useCompanyProfile(enabled: boolean = true) {
    return useQuery({
        queryKey: COMPANY_PROFILE_QUERY_KEY,
        queryFn: async (): Promise<CompanyProfile> => {
            return await apiGet<CompanyProfile>("/company-profile/stats");
        },
        enabled,
        staleTime: 30 * 60 * 1000, // 30 minutes - company profile changes less frequently
        refetchInterval: 60 * 60 * 1000, // Refresh every hour
    });
}
